/* eslint-disable react/prop-types */
import styled from '@emotion/styled';

import Picture from './oc-picture';
import Text from './oc-text';
import { isUrlCloudinary, CloudinaryDefaultConfig } from '../../helpers/cloudfront_helper';
import useDeviceBreakpoint from '../../hooks/use_device_breakpoint';

const getSpan = arr => {
  let str = arr[0];
  if (arr[1] !== 0) {
    str += ` / span ${arr[1]}`;
  }
  return str;
};

const HeroContainer = styled.div`
  display: inline-block;
  position: relative;
  width: 100%;
`;

const HeroBackground = styled.div`
  & {
    height: 100%;
    position: absolute;
    width: 100%;
    z-index: -1;
  }

  &.hero-bg {
    ${props => props.mediaType === 'image' && `background-image: url('${props.imgTablet}');`}

    @media (min-width: 1200px) {
      ${props => props.mediaType === 'image' && `background-image: url('${props.imgDesktop}');`}
    }
  }

  video {
    height: 100%;
    object-fit: cover;
    width: 100%;
  }
`;

const Hero = styled.div`
  &.hero {
    height: 100%;
    left: 0;
    margin-bottom: 20px;
    max-width: 100%;
    position: absolute;
    top: 0;
    width: 100%;
    z-index: -1;

    .fill {
      margin: -1px;
    }

    br {
      @media (min-width: 768px) and (max-width: 1200px) {
        display: none;
      }
    }

    grid-template-columns: repeat(3, 1fr);
    grid-template-rows: repeat(2, auto);

    @media (max-width: 767px) {
      padding: 0 15px;
      ${props => props.grid.mobile.css}
    }

    @media (min-width: 768px) {
      margin-bottom: 0;
      grid-template-columns: ${props => props.grid.tablet.columns};
      grid-template-rows: ${props => props.grid.tablet.rows};
      ${props => `grid-template-areas: \"${props.fill.tabletTranslated.join('" "')}\";`}
    }

    @media (min-width: 1200px), screen and (-ms-high-contrast: active), screen and (-ms-high-contrast: none) {
      -ms-grid-columns: ${props => props.grid.desktop.ms_columns};
      -ms-grid-rows: ${props => props.grid.desktop.ms_rows};
      grid-template-columns: ${props => props.grid.desktop.columns};
      grid-template-rows: ${props => props.grid.desktop.rows};

      ${props => `grid-template-areas: \"${props.fill.desktopTranslated.join('" "')}\";`}
    }

    @media screen and (-ms-high-contrast: active), screen and (-ms-high-contrast: none) {
      ${props =>
        Object.keys(props.fill.desktop)
          .filter(k => k !== 'dim')
          .map(k => {
            const classMap = {
              tf: 'top-fill',
              ltf: 'left-top-fill',
              lf: 'left-fill',
              mf: 'middle-fill',
              rf: 'right-fill',
              rbf: 'right-bottom-fill',
              lbf: 'left-bottom-fill',
              bf: 'bottom-fill',
            };
            return `.${classMap[k]} {
            -ms-grid-row: ${props.fill.desktop[k][0] + 1};
            -ms-grid-row-span: ${props.fill.desktop[k][1] + 1};
            -ms-grid-column: ${props.fill.desktop[k][2] + 1};
            -ms-grid-column-span: ${props.fill.desktop[k][3] + 1};
          }
        `;
          })}
    }
  }
`;

const Heading = styled.h1`
  & {
    margin-top: 0px !important;
  }
`;

const Main = styled.div`
  &.main {
    padding: 0 5%;
    position: relative;
    text-align: right;
    z-index: 1;
    ${props => props.pos.mobile.css}

    @media (min-width: 768px) {
      padding: 0 20%;
      ${props => props.pos.tablet.css}
    }

    @media screen and (min-width: 1200px) {
      padding: 0 22.5%;
      ${props => props.pos.desktop.css}
    }
  }
`;

const Panther = styled.div`
  &.panther {
    background: transparent;
    grid-column: ${props => getSpan(props.pos.mobile.column)};
    grid-row: ${props => getSpan(props.pos.mobile.row)};
    height: 115px;
    position: relative;
    width: auto;
    z-index: 2;

    &:before {
      background-image: url('${props =>
        Picture.Transform(props.panther) ||
        'https://imagecdn.owenscorning.com/ocimages/image/upload/f_auto/v1621524256/roofing/artist-panther.png'}');
      left: 0;
      ${props =>
        props.pos.mobile.size && `background-size: ${`${props.pos.mobile.size[0]}px ${props.pos.mobile.size[1]}px`};`}
      ${props => props.pos.mobile.css}

      @media (min-width: 768px) {
        background-size: ${props => `${props.pos.tablet.size[0]}px ${props.pos.tablet.size[1]}px`};
        background-position: left bottom;
        ${props => props.pos.tablet.css}
      }

      @media (min-width: 1200px), screen and (-ms-high-contrast: active), screen and (-ms-high-contrast: none) {
        background-size: ${props => `${props.pos.desktop.size[0]}px ${props.pos.desktop.size[1]}px`};
        background-position: left bottom;
        ${props => props.pos.desktop.css}
      }
    }

    @media (min-width: 768px) {
      grid-column: ${props => getSpan(props.pos.tablet.column)};
      grid-row: ${props => getSpan(props.pos.tablet.row)};
      height: auto;
    }

    @media screen and (min-width: 1200px) {
      grid-column: ${props => getSpan(props.pos.desktop.column)};
      grid-row: ${props => getSpan(props.pos.desktop.row)};
    }

    @media screen and (-ms-high-contrast: active), screen and (-ms-high-contrast: none) {
      -ms-grid-column: ${props => props.pos.desktop.column[0]};
      -ms-grid-column-span: ${props => props.pos.desktop.column[1]};
      -ms-grid-row: ${props => props.pos.desktop.row[0]};
      -ms-grid-row-span: ${props => props.pos.desktop.row[1]};
    }
  }
`;

const TextContent = styled.div`
  &.text-content {
    line-height: 1.3;
    overflow: hidden;
    padding-left: 0;
    ${props => props.pos.mobile.css}

    @media (min-width: 768px) {
      padding-left: 140px;
      ${props => props.pos.tablet.css}

      .body-text {
        ${props => props.pos.tablet.textCss}
      }

      a {
        display: inline-flex;
      }
    }

    @media (min-width: 1200px), screen and (-ms-high-contrast: active), screen and (-ms-high-contrast: none) {
      padding-left: 260px;
      ${props => props.pos.desktop.css}

      .body-text {
        ${props => props.pos.desktop.textCss}
      }
    }

    .body-text {
      ${props => props.pos.mobile.textCss}
      text-align: left;
    }

    .oc-cta-container {
      padding: 0;
      margin: 0;

      a {
        min-width: 150px;
      }

      @media screen and (min-width: 768px) {
        &:last-of-type {
          margin-right: 0;

          a {
            margin-bottom: 0;
          }
        }
      }

      @media screen and (min-width: 1200px) {
        margin-right: 16px;

        a {
          min-width: 140px;
        }
      }
    }

    div {
      margin: 0;
    }

    a {
      margin-bottom: 16px;
    }

    .button-wrapper {
      align-items: center;
      display: flex;
      flex-direction: column;
      margin-top: 16px;
      width: auto;

      @media screen and (min-width: 768px) {
        align-items: flex-start;
        margin-top: 24px;
      }

      @media screen and (min-width: 1200px),
        screen and (-ms-high-contrast: active),
        screen and (-ms-high-contrast: none) {
        flex-direction: row;
        justify-content: flex-end;
        margin-top: 32px;
      }
    }
  }
`;

const translateFill = grid => {
  const populateArr = (specFill, specGrid) => {
    const arr = new Array(specGrid.dim[1]).fill('.').map(() => new Array(specGrid.dim[0]).fill('.'));
    Object.keys(specFill).forEach(fk => {
      if (fk === 'dim') return;
      const [xMin, xSpan, yMin, ySpan] = specFill[fk];
      for (let y = yMin; y <= yMin + ySpan; y++) {
        for (let x = xMin; x <= xMin + xSpan; x++) {
          arr[y][x] = fk;
        }
      }
    });
    return arr;
  };

  const joinArr = arr => arr.map(row => row.join(' '));

  const tablet = joinArr(populateArr(grid.fill.tablet, grid.tablet));
  const desktop = joinArr(populateArr(grid.fill.desktop, grid.desktop));

  return [tablet, desktop];
};

const getTabletSizedVideoUrl = url => {
  if (!isUrlCloudinary(url)) {
    return null;
  }

  const videoId = url
    .toLowerCase()
    .replace(`https://${CloudinaryDefaultConfig.secure_distribution}/`, '')
    .replace(`${CloudinaryDefaultConfig.cloud_name}/`, '')
    .replace(`https://res.cloudinary.com/ocimages/`, '')
    .replace(`video/upload/`, '');

  const transformedUrl = `https://${CloudinaryDefaultConfig.secure_distribution}/video/upload/q_auto:eco/c_scale,w_798/${videoId}`;
  return transformedUrl;
};

const HeroT1 = ({
  prehead,
  heading,
  body,
  ctas,
  panther,
  pantherAlt,
  grid,
  imgDesktop,
  imgTablet,
  imgAlt,
  videoWebm,
  videoMp4,
  mediaType,
}) => {
  const [tabletTranslated, desktopTranslated] = translateFill(grid);
  grid.fill.tabletTranslated = tabletTranslated;
  grid.fill.desktopTranslated = desktopTranslated;
  const processedImgDesktop = Picture.DefaultOrCrop(imgDesktop?.file, imgDesktop?.crop, 'http://placehold.it/1340x700');
  const processedImgTablet = Picture.DefaultOrCrop(imgTablet?.file, imgTablet?.crop, 'http://placehold.it/800x480');
  const imgDesktopAlt = imgDesktop && imgDesktop?.alt ? imgDesktop?.alt : imgAlt;
  const { isDesktop } = useDeviceBreakpoint();
  const videoTabletWebm = getTabletSizedVideoUrl(videoWebm);
  const videoTabletMp4 = getTabletSizedVideoUrl(videoMp4);

  return (
    <HeroContainer className="hero-tier-1">
      <HeroBackground
        aria-label={mediaType == 'image' ? imgDesktopAlt : null}
        className="hero-video-background video hero-bg"
        imgDesktop={processedImgDesktop}
        imgTablet={processedImgTablet}
        mediaType={mediaType}
        role={mediaType == 'image' ? 'img' : null}
      >
        {mediaType === 'video' && (
          <video playsInline muted autoPlay>
            <source src={isDesktop ? videoWebm : videoTabletWebm} type="video/webm" />
            <source src={isDesktop ? videoMp4 : videoTabletMp4} type="video/mp4" />
          </video>
        )}
      </HeroBackground>
      <Main className="main rball-header" pos={grid.main}>
        <Hero className="hero" fill={grid.fill} grid={grid}>
          <Panther className="panther" pos={grid.panther} panther={panther} aria-label={pantherAlt} role="img" />
          <div className="fill top-fill" />
          <div className="fill left-top-fill" />
          <div className="fill left-fill" />
          <div className="fill middle-fill" />
          <div className="fill right-fill" />
          <div className="fill right-bottom-fill" />
          <div className="fill left-bottom-fill" />
          <div className="fill bottom-fill" />
        </Hero>

        {prehead && (
          <span className="prehead">
            <Text content={prehead} />
          </span>
        )}
        <Heading>{heading}</Heading>
        <TextContent pos={grid.textContent} className="text-content">
          <div className="body-text">{body}</div>
          <div className="button-wrapper">{ctas}</div>
        </TextContent>
      </Main>
    </HeroContainer>
  );
};

export default HeroT1;
